import React, { useState, useRef } from 'react';
import CreateForm from '../forms/CreateForm';
import Footer from '../layout/Footer';
import colors from '../../utils/colors';

const CustomBottomSheet = ({ 
  onCreateSong, 
  credits,
  coverSongData,
  onCoverSongDataUsed,
  extendSongData,
  onExtendSongDataUsed,
  replaceSectionData,
  onReplaceSectionDataUsed,
  reuseLyricsData,
  onReuseLyricsDataUsed,
  introSongData,
  onIntroSongDataUsed,
  speedSongData,
  onSpeedSongDataUsed,
  onExpandedChange
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Notify parent when expanded state changes
  React.useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [formData, setFormData] = useState({});
  const sheetRef = useRef(null);
  const createFormRef = useRef(null);

  const handleCreateAgain = () => {
    console.log('Create Again clicked from custom bottom sheet');
  };

  const handleEditPrompt = () => {
    setIsExpanded(true);
  };

  const handleCreateSong = (songData) => {
    onCreateSong(songData);
    setIsExpanded(false); // Collapse after creating
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setStartY(clientY);
    setCurrentY(clientY);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    setCurrentY(clientY);
    
    const deltaY = clientY - startY;
    
    if (isExpanded && deltaY > 50) {
      // Dragging down when expanded - collapse
      setIsExpanded(false);
      setIsDragging(false);
    } else if (!isExpanded && deltaY < -50) {
      // Dragging up when collapsed - expand
      setIsExpanded(true);
      setIsDragging(false);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add event listeners for mouse/touch events
  React.useEffect(() => {
    const handleMouseMove = (e) => handleDragMove(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, startY]);

  return (
    <>
      <div
        ref={sheetRef}
        className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ease-out`}
        style={{ 
          height: isExpanded ? 'calc(100vh - 40px)' : '102px',
          maxHeight: 'calc(100vh - 40px)',
          transform: isExpanded ? 'translateY(0)' : 'translateY(calc(100% - 102px))',
          backgroundColor: isExpanded ? colors.bg.primary : colors.bg.tertiary
        }}
      >
        {/* Scrollable Content Area - Only visible when expanded */}
        {isExpanded && (
          <div className="flex-1 overflow-y-auto pb-24">
            <CreateForm 
              onCreateSong={handleCreateSong}
              credits={credits}
              coverSongData={coverSongData}
              onCoverSongDataUsed={onCoverSongDataUsed}
              extendSongData={extendSongData}
              onExtendSongDataUsed={onExtendSongDataUsed}
              replaceSectionData={replaceSectionData}
              onReplaceSectionDataUsed={onReplaceSectionDataUsed}
              reuseLyricsData={reuseLyricsData}
              onReuseLyricsDataUsed={onReuseLyricsDataUsed}
              introSongData={introSongData}
              onIntroSongDataUsed={onIntroSongDataUsed}
              speedSongData={speedSongData}
              onSpeedSongDataUsed={onSpeedSongDataUsed}
              hasPlaybar={false}
              isMobile={true}
              onSlideDown={() => setIsExpanded(false)}
              hideFooter={true} // Hide CreateForm's internal footer
            />
          </div>
        )}

        {/* Drag Handle - Always visible at top when collapsed */}
        {!isExpanded && (
          <div 
            className="flex justify-center items-center w-full h-[16px] pt-[6px] cursor-grab active:cursor-grabbing"
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <div className="w-[36px] h-[4px] rounded-[40px] bg-[#726e6c]" />
          </div>
        )}
      </div>
      
      {/* Morphing Buttons - Fixed to screen bottom, outside tray */}
      <div className={`fixed bottom-0 left-0 right-0 z-[60] transition-all duration-300 ease-out`}
           style={{
             backgroundColor: isExpanded ? colors.bg.primary : colors.bg.tertiary
           }}>
      <div 
        className={`flex justify-center items-center w-full px-4 transition-all duration-300 ease-out ${
          isExpanded ? 'h-[80px] pb-6' : 'h-[86px] pb-6'
        }`}
        style={{
          backgroundColor: isExpanded ? colors.bg.primary : colors.bg.tertiary
        }}
      >
        <div className={`flex items-center w-full max-w-[402px] ${
          isExpanded ? 'justify-start gap-2' : 'justify-center gap-2'
        }`}>
          {/* Morphing Left Button: Edit Prompt ↔ Clear/Trash */}
          <div 
            className={`flex justify-center items-center h-[56px] gap-2 px-6 rounded-full cursor-pointer transition-all duration-300 ease-out border border-white/10 ${
              isExpanded 
                ? 'w-[56px] flex-grow-0 flex-shrink-0' 
                : 'flex-1'
            }`}
            style={{
              backgroundColor: colors.bg.glass.thick
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = colors.bg.glass.dense}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.bg.glass.thick}
            onClick={isExpanded ? () => {/* TODO: Clear all */} : handleEditPrompt}
          >
            {isExpanded ? (
              // Trash icon only - centered
              <div className="w-4 h-4 flex items-center justify-center">
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                >
                  <path
                    d="M6 2C6 1.44772 6.44772 1 7 1H9C9.55228 1 10 1.44772 10 2V3H11.5C11.7761 3 12 3.22386 12 3.5C12 3.77614 11.7761 4 11.5 4H11V12C11 13.1046 10.1046 14 9 14H7C5.89543 14 5 13.1046 5 12V4H4.5C4.22386 4 4 3.77614 4 3.5C4 3.22386 4.22386 3 4.5 3H6V2ZM7 3H9V2H7V3ZM6 4V12C6 12.5523 6.44772 13 7 13H9C9.55228 13 10 12.5523 10 12V4H6Z"
                    fill="#FAF7F5"
                  />
                </svg>
              </div>
            ) : (
              // Edit icon and text - prevent wrapping with nowrap
              <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                  >
                    <path
                      d="M2.66667 14C2.29848 14 2 13.7015 2 13.3333V11.4432C2 11.2661 2.07041 11.0964 2.19571 10.9713L10.8 2.38333C10.9333 2.26111 11.0806 2.16667 11.2417 2.1C11.4028 2.03333 11.5722 2 11.75 2C11.9278 2 12.1 2.03333 12.2667 2.1C12.4333 2.16667 12.5778 2.26667 12.7 2.4L13.6167 3.33333C13.75 3.45556 13.8472 3.6 13.9083 3.76667C13.9694 3.93333 14 4.1 14 4.26667C14 4.44444 13.9694 4.61389 13.9083 4.775C13.8472 4.93611 13.75 5.08333 13.6167 5.21667L5.0286 13.8047C4.90357 13.9298 4.734 14 4.55719 14H2.66667ZM11.7333 5.2L12.6667 4.26667L11.7333 3.33333L10.8 4.26667L11.7333 5.2Z"
                      fill="#FAF7F5"
                    />
                  </svg>
                </div>
                <span className="flex-shrink-0" style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  letterSpacing: '0.005em',
                  lineHeight: '20px',
                  color: '#BAB4B1'
                }}>Edit Prompt</span>
              </div>
            )}
          </div>
          
          {/* Morphing Right Button: Create Again ↔ Create */}
          <div 
            className={`flex justify-center items-center h-[56px] gap-2 px-6 rounded-full cursor-pointer hover:brightness-110 transition-all duration-300 ease-out ${
              isExpanded ? 'flex-grow' : 'flex-1'
            }`}
            style={{
              backgroundImage: "url('/aura.png')",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              color: "#FAF7F5"
            }}
            onClick={isExpanded ? () => handleCreateSong({}) : handleCreateAgain}
          >
            {isExpanded ? (
              // Create icon and text for expanded state
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                  >
                    <path d="M8 3V13M3 8H13" stroke="#FAF7F5" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  letterSpacing: '0.005em',
                  lineHeight: '28px'
                }}>Create</span>
              </>
            ) : (
              // Create Again icon and text for collapsed state
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                  >
                    <path
                      d="M6.45412 4.87567L4.57215 5.06387C4.23135 5.09795 3.97182 5.38472 3.97182 5.72723V11.5626C3.84244 11.5415 3.70853 11.5305 3.5714 11.5305C2.46569 11.5305 1.56934 12.2476 1.56934 13.1322C1.56934 14.0167 2.46569 14.7338 3.5714 14.7338C4.67711 14.7338 5.57347 14.0167 5.57347 13.1322V8.16704L9.24297 7.80009C9.22204 7.75682 9.20222 7.71237 9.18359 7.66674L8.74374 6.58942L7.66642 6.14957C7.04211 5.89467 6.63801 5.41786 6.45412 4.87567Z"
                      fill="#FAF7F5"
                    />
                    <path
                      d="M10.3784 8.85048V10.7617C10.2491 10.7407 10.1151 10.7297 9.97802 10.7297C8.87231 10.7297 7.97595 11.4468 7.97595 12.3313C7.97595 13.2159 8.87231 13.933 9.97802 13.933C11.0837 13.933 11.9801 13.2159 11.9801 12.3313V8.8348C11.4709 9.04199 10.8912 9.04721 10.3784 8.85048Z"
                      fill="#FAF7F5"
                    />
                    <path
                      d="M7.69282 4.35688C7.68001 4.04294 7.84582 3.72229 8.19026 3.58166L9.47473 3.05724C9.67397 2.97589 9.83209 2.81777 9.91344 2.61853L10.4379 1.33406C10.709 0.669897 11.6495 0.669896 11.9207 1.33406L12.4451 2.61853C12.5265 2.81777 12.6846 2.97589 12.8838 3.05724L14.1683 3.58166C14.4329 3.68971 14.5921 3.90403 14.6459 4.13994C14.6587 4.45388 14.4929 4.77453 14.1484 4.91515L12.864 5.43958C12.6647 5.52092 12.5066 5.67904 12.4253 5.87829L11.9008 7.16276C11.6297 7.82692 10.6892 7.82692 10.418 7.16276L9.89358 5.87829C9.81224 5.67904 9.65412 5.52092 9.45487 5.43958L8.1704 4.91515C7.90576 4.8071 7.74657 4.59278 7.69282 4.35688Z"
                      fill="#FAF7F5"
                    />
                  </svg>
                </div>
                <span style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  letterSpacing: '0.005em',
                  lineHeight: '28px'
                }}>Create Again</span>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default CustomBottomSheet;